import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import InfoTip from './InfoTip'

/** How many addresses a teacher may give; the server holds the same number (D-725). */
const MAX_LINKS = 4

const tidy = (links: string[]) => links.map((l) => l.trim()).filter((l) => l !== '')

/**
 * Where a family pays her for a private (D-662).
 *
 * **Owner, from the pilot:** *"Teachers want to attach a Venmo or Cash App link
 * to their profiles, used when parents schedule a makeup or private. The parent
 * is told at booking that they pay out of pocket."*
 *
 * **On her own account screen, beside the PIN**, because it is hers in the same
 * way: nobody else's business to set, and the studio holding it behind Tonya is
 * what makes a change a text message. `ManageStaff` can still set it on the
 * staff record, for the same reason it can correct an email (D-613) — a teacher
 * who cannot sign in yet still has to be reachable.
 *
 * **The app shows a link and never moves money** (D-54). Nothing here knows
 * whether a family paid; that is what the teacher's own question answers later.
 *
 * **Silent for a parent**, who has no staff row and nothing to set.
 */
export default function PaymentLinkPanel({
  isStaff,
  isKiosk,
  paymentLinks,
}: {
  isStaff: boolean
  isKiosk: boolean
  /** What the session says she has set, in her order. */
  paymentLinks: string[]
}) {
  /*
   * <b>Several addresses</b> (D-725). `asked`: <i>"we should allow adding more than
   * one in the case they have say venmo and cashapp and paypal"</i>. Always at
   * least one box, so an empty list still has somewhere to type.
   */
  const [links, setLinks] = useState<string[]>(paymentLinks.length > 0 ? paymentLinks : [''])
  const [error, setError] = useState<string | null>(null)
  /*
   * What the write actually did, not what the session says (D-662).
   *
   * Keyed off the session prop, the sentence announced the opposite of what
   * had just happened: setSaved renders at once and the refetch lands a round
   * trip later, so a first-ever save said "Cleared. Families will not be shown
   * a link." in a role="status" - and after a deploy, when every endpoint
   * refuses for ~75 seconds and the retry gives up in about a second (D-33),
   * it said so permanently. That is D-209 on her own confirmation.
   */
  const [saved, setSaved] = useState<{ count: number } | null>(null)

  const queryClient = useQueryClient()

  /*
   * The session is the source; the box follows it.
   *
   * For a value that changed somewhere else - the studio setting one for her
   * while she has the screen open (D-662 gives ManageStaff that door). It does
   * nothing on a failed save, deliberately: the text she typed is still the
   * text she meant, and clearing it would lose her work to a network blip.
   */
  // Keyed on the joined value, so a new array with the same addresses is no change.
  const stored = paymentLinks.join('\n')
  useEffect(() => {
    setLinks(stored === '' ? [''] : stored.split('\n'))
  }, [stored])

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/teacher/payment-link', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentLinks: tidy(links) }),
      })

      if (!res.ok) {
        const problem = await res.json().catch(() => null)
        throw new Error(problem?.detail ?? 'That could not be saved.')
      }

      return res.json()
    },
    onSuccess: async () => {
      setError(null)
      setSaved({ count: tidy(links).length })
      // The session carries it, so the whole app agrees about what she has set.
      await queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (e: Error) => {
      setError(e.message)
      setSaved(null)
    },
  })

  if (!isStaff || isKiosk) return null

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setSaved(null)
    setError(null)
    save.mutate()
  }

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-4">
      {/* The measure its three neighbours on this screen use, not a page heading. */}
      <div className="flex flex-wrap items-center gap-1">
        <h2 className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Getting paid for privates
        </h2>
        <InfoTip label="More about getting paid">
          Families see this link when they book a private or a makeup with you, and on the lesson
          on their schedule. They pay you directly &mdash; the studio takes no part in it.
        </InfoTip>
      </div>

      {/*
        <b>It says where it appears, because it is her handle and her consent</b>
        (D-691). This read *"when they book"* until the family's schedule began
        carrying it — true of the message and no longer the whole truth.

        <b>And it does not promise the link goes away</b>, which a first attempt
        at this sentence did. The schedule endpoint deliberately does not clamp
        its window to today, so a parent paging back through her season sees a
        past lesson and its payment line. That is right rather than a leak: this
        kind chases *after* the lesson (`OnClosingDate`, D-666), so a family
        still owing for September needs somewhere to pay.
      */}
      <p className="mt-1 text-[13px] text-ink-2">Your Venmo, Cash App or PayPal links, so families can pay you.</p>

      <form onSubmit={submit} className="mt-3 flex flex-wrap items-end gap-2">
        <div className="flex min-w-[14rem] flex-1 flex-col gap-1.5">
          {links.map((value, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input
                value={value}
                onChange={(e) => {
                  setLinks(links.map((l, j) => (j === i ? e.target.value : l)))
                  setSaved(null)
                }}
                maxLength={500}
                inputMode="url"
                aria-label={links.length === 1 ? 'Your payment link' : `Your payment link ${i + 1}`}
                placeholder="https://venmo.com/u/..."
                className="min-h-[40px] min-w-0 flex-1 rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[13px]"
              />
              {links.length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    setLinks(links.filter((_, j) => j !== i))
                    setSaved(null)
                  }}
                  aria-label={`Remove payment link ${i + 1}`}
                  title="Remove this link"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-3 hover:bg-surface-2 hover:text-crit"
                >
                  <X size={15} aria-hidden="true" />
                </button>
              )}
            </div>
          ))}

          {links.length < MAX_LINKS && (
            <button
              type="button"
              onClick={() => setLinks([...links, ''])}
              className="flex min-h-[32px] items-center gap-1 self-start text-[12.5px] font-semibold text-accent hover:underline"
            >
              <Plus size={14} aria-hidden="true" />
              Add another link
            </button>
          )}
        </div>

        <button
          type="submit"
          disabled={save.isPending || tidy(links).join('\n') === stored}
          className="min-h-[40px] rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent disabled:opacity-50"
        >
          {save.isPending ? 'Saving...' : 'Save'}
        </button>
      </form>

      {/*
        Clearing it is a real answer, so it says so rather than leaving her to
        wonder whether an empty box saved.
      */}
      {saved && (
        <p role="status" className="mt-2 text-[12.5px] font-semibold text-good">
          {saved.count > 0 ? 'Saved.' : 'Cleared. Families will not be shown a link.'}
        </p>
      )}

      {error && <p className="mt-2 text-[12.5px] text-crit">{error}</p>}
    </section>
  )
}
